/**
 * Utility for parsing and extracting email headers.
 * Ports header extraction logic from MailItemExtensions.cs.
 */
export class HeaderParser {
  /**
   * Extract headers from Office.js mailbox item.
   * Office.js provides headers via the getAllInternetHeadersAsync API.
   * @param mailbox - Office mailbox context
   * @returns Promise resolving to raw headers string
   */
  static async getHeaders(mailbox: Office.Mailbox): Promise<string> {
    return new Promise((resolve) => {
      const item = mailbox.item;
      if (!item) {
        resolve('');
        return;
      }

      // Check if getAllInternetHeadersAsync is available (Mailbox 1.8+)
      if (item.getAllInternetHeadersAsync) {
        item.getAllInternetHeadersAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve(result.value || '');
          } else {
            console.warn('Failed to get internet headers:', result.error?.message);
            resolve('');
          }
        });
      } else {
        // Fallback: try to get basic header info
        resolve(this.buildBasicHeaders(item));
      }
    });
  }

  /**
   * Build basic headers from available item properties when full headers aren't available.
   */
  private static buildBasicHeaders(item: Office.MessageRead): string {
    const headers: string[] = [];

    if (item.subject) {
      headers.push(`Subject: ${item.subject}`);
    }

    if (item.from?.emailAddress) {
      const from = item.from.displayName
        ? `"${item.from.displayName}" <${item.from.emailAddress}>`
        : item.from.emailAddress;
      headers.push(`From: ${from}`);
    }

    if (item.to && item.to.length > 0) {
      const toAddresses = item.to
        .map((r) => (r.displayName ? `"${r.displayName}" <${r.emailAddress}>` : r.emailAddress))
        .join(', ');
      headers.push(`To: ${toAddresses}`);
    }

    if (item.cc && item.cc.length > 0) {
      const ccAddresses = item.cc
        .map((r) => (r.displayName ? `"${r.displayName}" <${r.emailAddress}>` : r.emailAddress))
        .join(', ');
      headers.push(`Cc: ${ccAddresses}`);
    }

    if (item.dateTimeCreated) {
      headers.push(`Date: ${item.dateTimeCreated.toUTCString()}`);
    }

    if (item.internetMessageId) {
      headers.push(`Message-ID: ${item.internetMessageId}`);
    }

    if (item.conversationId) {
      headers.push(`Thread-Index: ${item.conversationId}`);
    }

    return headers.join('\r\n');
  }

  /**
   * Parse a specific header value from raw headers string.
   * @param headers - Raw headers string
   * @param headerName - Name of header to extract (case-insensitive)
   * @returns Header value or empty string if not found
   */
  static parseHeaderValue(headers: string, headerName: string): string {
    const regex = new RegExp(`^${headerName}:\\s*(.*)$`, 'im');
    const match = headers.match(regex);
    return match ? match[1].trim() : '';
  }

  /**
   * Extract all "Received" headers (useful for tracing email path).
   */
  static extractReceivedHeaders(headers: string): string[] {
    const lines = headers.split(/\r?\n/);
    const received: string[] = [];
    let currentReceived = '';

    for (const line of lines) {
      if (line.match(/^Received:/i)) {
        if (currentReceived) {
          received.push(currentReceived.trim());
        }
        currentReceived = line;
      } else if (currentReceived && (line.startsWith(' ') || line.startsWith('\t'))) {
        // Continuation of previous header
        currentReceived += ' ' + line.trim();
      } else if (currentReceived) {
        received.push(currentReceived.trim());
        currentReceived = '';
      }
    }

    if (currentReceived) {
      received.push(currentReceived.trim());
    }

    return received;
  }

  /**
   * Check if email appears to be external (from outside the organization).
   * Looks for common external email indicators in headers.
   */
  static isExternalEmail(headers: string): boolean {
    // Check for common external email indicators
    const externalIndicators = [
      /X-MS-Exchange-Organization-AuthSource:/i,
      /X-MS-Exchange-CrossTenant-AuthAs:\s*Anonymous/i,
      /X-Originating-IP:/i,
      /X-MS-Exchange-Organization-SCL:\s*[1-9]/i,
    ];

    return externalIndicators.some((regex) => regex.test(headers));
  }

  /**
   * Extract sender's IP address from headers if available.
   */
  static extractSenderIP(headers: string): string | null {
    // Check X-Originating-IP
    let match = headers.match(/X-Originating-IP:\s*\[?([^\]\r\n]+)\]?/i);
    if (match) return match[1].trim();

    // Check first Received header for IP
    const receivedHeaders = this.extractReceivedHeaders(headers);
    if (receivedHeaders.length > 0) {
      match = receivedHeaders[receivedHeaders.length - 1].match(/\[(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})\]/);
      if (match) return match[1];
    }

    return null;
  }
}
